import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Container from '@mui/material/Container';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';

// project-imports
import Drawer from './Drawer';
import Header from './Header';
import HorizontalBar from './Drawer/HorizontalBar';
import Loader from 'components/Loader';

import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { CONTENT_OFFSET, DRAWER_WIDTH, MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import AuthGuard from 'utils/route-guard/AuthGuard';
import { HeaderActionsProvider } from 'contexts/HeaderActionsContext';
import { useHeaderActions } from 'hooks/useHeaderActions';
import { clacUiGlobal as ui } from 'utils/uiGlobal';

function MainContentContainer() {
  const { fullHeightContent } = useHeaderActions();
  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        position: 'relative',
        minHeight: `calc(100vh - ${CONTENT_OFFSET}px)`,
        ...(fullHeightContent && { height: `calc(100vh - ${CONTENT_OFFSET}px)`, overflow: 'hidden' }),
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Outlet />
    </Container>
  );
}

// ==============================|| MAIN LAYOUT ||============================== //

export default function MainLayout() {
  const { menuMasterLoading } = useGetMenuMaster();
  const downXL = useMediaQuery((theme) => theme.breakpoints.down('xl'));
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const { miniDrawer, menuOrientation } = useConfig();

  const isHorizontal = menuOrientation === MenuOrientation.HORIZONTAL && !downLG;

  // set media wise responsive drawer
  useEffect(() => {
    if (!miniDrawer) {
      handlerDrawerOpen(!downXL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downXL]);

  if (menuMasterLoading) return <Loader />;

  return (
    <AuthGuard>
      <HeaderActionsProvider>
        <Box sx={{ display: 'flex', width: '100%' }}>
          {/* El shell de navegación queda fuera del alcance de Google Translate: sus textos ya
              tienen traducción propia vía react-intl, y contiene datos que no deben alterarse
              (nombre del usuario, siglas de grupo). `display: contents` evita que este wrapper
              genere caja y altere el layout flex. Ver components/GoogleTranslate. */}
          <Box className="notranslate" translate="no" sx={{ display: 'contents' }}>
            <Header />
            {!isHorizontal ? <Drawer /> : <HorizontalBar />}
          </Box>

          <Box component="main" sx={{ width: `calc(100% - ${DRAWER_WIDTH}px)`, flexGrow: 1, p: 0, bgcolor: ui.semantic.bg.page }}>
            <Toolbar
              sx={{
                minHeight: ui.layout.topbarHeight,
                height: ui.layout.topbarHeight,
                mt: isHorizontal ? 8 : 0,
                mb: isHorizontal ? 2 : 0
              }}
            />
            <MainContentContainer />
          </Box>
        </Box>
      </HeaderActionsProvider>
    </AuthGuard>
  );
}
